/**
 * Weekly open hours (Location.hours). Owner, 2026-09-29: "Opens at / closes at needs M–F options and different hours on
 * different days: a picker where, if one day has different hours or is closed all day, they can quickly modify it."
 *
 * | Rule | Why |
 * |---|---|
 * | H1 `hours` is optional. Absent (or a day missing): Location.openAt/closeAt apply that day | Every older row and client keeps working unchanged |
 * | H2 A day is `{ open, close }` or "closed". Closed → nothing is due, late or missed that day | A café closed Sunday isn't "missing" Sunday's checks |
 * | H3 close earlier than open means the next day (a bar 16:00–01:00). open === close is refused | Overnight places exist; a zero-length day is a typo |
 * | H4 Every place that used the single pair asks hoursOn (or the helpers below) | One rule, so Today, the phone, nags and analytics agree |
 * | H5 After midnight, an overnight day is still yesterday's shift until an hour past its close | 00:30 at a bar is closing time, not "before opening" |
 * | H6 A day may have 2 to 4 time ranges (lunch 11–2, dinner 5–10). open/close are then the first open and the last close, and every check runs over that whole span | Food is still held in the break: the fridge log and the closing checks don't stop at 2 PM (owner, 2026-10-09; persona review 2026-10-01 §2.2 "a 2-5 close can't be entered") |
 * | H7 `hours.dates` (special days: holidays, events) beats the weekday for that date, everywhere hoursOn is read | Thanksgiving closed means nothing is due or late on Thanksgiving, on every screen (owner, 2026-10-09) |
 * | H8 `hours.templates` are saved, named day hours ("Thanksgiving: 11 to 3"); applying one writes dates (H7). A template changes nothing by itself | One tap next year; nothing changes until the owner picks a date |
 *
 * Self-contained (no imports from schedule/analytics/feed) so schedule.ts can use it without a cycle; momentAt is in feed.ts.
 */
import type { IsoDate, IsoDateTime } from "./model.ts";

export const DAY_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const;
export type DayKey = (typeof DAY_KEYS)[number];
export const DAY_NAMES: Record<DayKey, string> = { mon: "Monday", tue: "Tuesday", wed: "Wednesday", thu: "Thursday", fri: "Friday", sat: "Saturday", sun: "Sunday" };
export const DAY_SHORT: Record<DayKey, string> = { mon: "Mon", tue: "Tue", wed: "Wed", thu: "Thu", fri: "Fri", sat: "Sat", sun: "Sun" };
export const WEEKDAY_KEYS: readonly DayKey[] = ["mon", "tue", "wed", "thu", "fri"];

export interface TimeRange { open: string; close: string }
/** One day's hours. `ranges` (H6): a split day, 2 to 4 ranges in time order; open/close are then its first open and last close. */
export interface OpenSpan extends TimeRange { ranges?: TimeRange[] }
export type DayHours = OpenSpan | "closed";
/** A special day (H7): its hours, and the holiday or template name it came from. */
export interface SpecialDay { hours: DayHours; label?: string; templateId?: string }
/** A saved, named set of day hours (H8): "Thanksgiving", "Christmas: closed", "Summer Fridays: open late". */
export interface HoursTemplate { id: string; name: string; hours: DayHours }
/** The week (mon … sun), plus special days by local date (H7) and saved templates (H8). Read it through hoursOn. */
export type WeeklyHours = Record<DayKey, DayHours> & { dates?: Record<IsoDate, SpecialDay>; templates?: HoursTemplate[] };
/** Anything with the fallback pair and (maybe) weekly hours: a Location, a DayContext, a /today location. */
export interface HoursSource { openAt: string; closeAt: string; hours?: WeeklyHours | null }

export const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;
export const toMinutes = (hhmm: string) => { const [h, m] = hhmm.split(":").map(Number); return (h ?? 0) * 60 + (m ?? 0); };
const hhmm = (mins: number) => `${String(Math.floor(mins / 60) % 24).padStart(2, "0")}:${String(mins % 60).padStart(2, "0")}`;
/** H3: closes after midnight. */
export const isOvernight = (s: OpenSpan) => toMinutes(s.close) < toMinutes(s.open);
const isDate = (s: string) => /^\d{4}-\d{2}-\d{2}$/.test(s);
export function addLocalDays(d: IsoDate, n: number): IsoDate {
  return new Date(Date.parse(`${d}T12:00:00Z`) + n * 86400000).toISOString().slice(0, 10);
}
/** A week from one function of the day (no special days, no templates). */
export function weekFrom(f: (k: DayKey) => DayHours): WeeklyHours {
  return { mon: f("mon"), tue: f("tue"), wed: f("wed"), thu: f("thu"), fri: f("fri"), sat: f("sat"), sun: f("sun") };
}
/** "mon" … "sun" for a local calendar date. */
export function dayKeyOf(date: IsoDate): DayKey {
  return DAY_KEYS[(new Date(`${date}T12:00:00Z`).getUTCDay() + 6) % 7]!;
}

const fmts = new Map<string, Intl.DateTimeFormat>();
/** Local date and minutes since local midnight in `tz`. */
export function localDateMinutes(at: Date | IsoDateTime, tz: string): { date: IsoDate; min: number } {
  let f = fmts.get(tz);
  if (!f) { f = new Intl.DateTimeFormat("en-US", { timeZone: tz, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }); fmts.set(tz, f); }
  const p: Record<string, string> = {};
  for (const x of f.formatToParts(typeof at === "string" ? new Date(at) : at)) p[x.type] = x.value;
  return { date: `${p.year}-${p.month}-${p.day}`, min: (Number(p.hour) % 24) * 60 + Number(p.minute) };
}

/**
 * The hours on one day (H1–H3). `when` is a local date ("2026-09-27"), or an instant read in `tz` (default UTC).
 * Returns "closed" or { open, close } as local "HH:MM"; close < open means it closes the next day.
 */
export function hoursOn(loc: HoursSource, when: IsoDate | IsoDateTime | Date, tz?: string): DayHours {
  const date = typeof when === "string" && isDate(when) ? when : localDateMinutes(when, tz ?? "UTC").date;
  // H7: a special day beats the weekday.
  const sp = loc.hours?.dates?.[date];
  if (sp && typeof sp === "object") { const d = cleanDay(sp.hours); if (d) return d; }
  return weekdayHours(loc, dayKeyOf(date));
}

/** The weekday's own hours, special days ignored (the picker, Google's regular hours). */
export function weekdayHours(loc: HoursSource, k: DayKey): DayHours {
  return cleanDay(loc.hours?.[k]) ?? { open: loc.openAt, close: loc.closeAt };
}

/** A stored day read safely: "closed", a span (with its ranges when they are good, H6), or null when it isn't one. */
function cleanDay(h: unknown): DayHours | null {
  if (h === "closed") return "closed";
  if (!h || typeof h !== "object") return null;
  const o = h as OpenSpan;
  if (typeof o.open !== "string" || typeof o.close !== "string" || !HHMM.test(o.open) || !HHMM.test(o.close)) return null;
  const r = Array.isArray(o.ranges) && o.ranges.length >= 2 && !rangesProblem(o.ranges) ? o.ranges : null;
  return r ? { open: r[0]!.open, close: r[r.length - 1]!.close, ranges: r.map((x) => ({ open: x.open, close: x.close })) } : { open: o.open, close: o.close };
}

/** A day's time ranges (H6): its ranges, or its one span. */
export function rangesOf(d: OpenSpan): TimeRange[] {
  return d.ranges && d.ranges.length >= 2 ? d.ranges.map((r) => ({ open: r.open, close: r.close })) : [{ open: d.open, close: d.close }];
}
/** A day from its ranges (H6): one range is a plain span; more carry `ranges`, with open/close the first open and last close. */
export function spanOfRanges(ranges: readonly TimeRange[]): OpenSpan {
  const r = ranges.map((x) => ({ open: x.open, close: x.close }));
  if (r.length <= 1) return r[0] ? { ...r[0] } : { ...DEFAULT_SPAN };
  return { open: r[0]!.open, close: r[r.length - 1]!.close, ranges: r };
}
export const MAX_RANGES = 4;
/** What's wrong with a day's ranges, in plain words, or null (H6): in time order, no overlap, only the last may pass midnight. */
export function rangesProblem(ranges: readonly TimeRange[]): string | null {
  if (!ranges.length) return "Add the hours.";
  if (ranges.length > MAX_RANGES) return `At most ${MAX_RANGES} time ranges a day.`;
  let prevClose = -1;
  for (let i = 0; i < ranges.length; i++) {
    const r = ranges[i]!;
    if (typeof r?.open !== "string" || typeof r?.close !== "string" || !HHMM.test(r.open) || !HHMM.test(r.close)) return "Hours look like 07:00.";
    if (r.open === r.close) return "Opening and closing can't be the same time.";
    const o = toMinutes(r.open), c = toMinutes(r.close);
    if (o <= prevClose) return "Each time range starts after the one before it ends.";
    if (c < o && i < ranges.length - 1) return "Only the last time range can close after midnight.";
    prevClose = c < o ? c + 1440 : c;
  }
  if (prevClose - toMinutes(ranges[0]!.open) >= 1440) return "A day can't be 24 hours or longer.";
  return null;
}

/** The day's open span in minutes from that day's midnight; an overnight close is past 1440 (H3). */
export function spanMinutes(s: OpenSpan): { openMin: number; closeMin: number } {
  const openMin = toMinutes(s.open), c = toMinutes(s.close);
  return { openMin, closeMin: c < openMin ? c + 1440 : c };
}

/** A day's hours as instants, using the caller's zone-aware toIso. Closed: a zero-length window at midnight. */
export function hoursIso(loc: HoursSource, date: IsoDate, toIso: (d: IsoDate, hhmm: string) => IsoDateTime): { open: IsoDateTime; close: IsoDateTime; closed?: true } {
  const h = hoursOn(loc, date);
  if (h === "closed") { const mid = toIso(date, "00:00"); return { open: mid, close: mid, closed: true }; }
  return { open: toIso(date, h.open), close: toIso(isOvernight(h) ? addLocalDays(date, 1) : date, h.close) };
}

/**
 * The open window that matters at `now`: yesterday's while an overnight shift is still running past midnight (H5),
 * else `date`'s. Quiet hours (nags, gentle nudges) read this.
 */
export function shiftHoursIso(loc: HoursSource, date: IsoDate, now: IsoDateTime, toIso: (d: IsoDate, hhmm: string) => IsoDateTime): { open: IsoDateTime; close: IsoDateTime; closed?: true } {
  const y = hoursIso(loc, addLocalDays(date, -1), toIso);
  if (!y.closed && y.close > toIso(date, "00:00") && now <= y.close) return y;
  return hoursIso(loc, date, toIso);
}

/** Local instant for a minute offset from `date`'s midnight (≥ 1440 lands on the next day). */
export function isoAtMinute(date: IsoDate, mins: number, toIso: (d: IsoDate, hhmm: string) => IsoDateTime): IsoDateTime {
  const days = Math.floor(mins / 1440);
  return toIso(days ? addLocalDays(date, days) : date, hhmm(mins - days * 1440));
}

export interface DayWindow {
  /** The operating day `now` belongs to (yesterday, after midnight inside an overnight shift: H5). */
  date: IsoDate;
  closed: boolean;
  /** Minutes since `date`'s midnight (past 1440 after midnight on an overnight shift). */
  nowMin: number;
  openMin: number;
  closeMin: number;
}

/** Where `now` falls in the location's day (H5). Closed: openMin/closeMin are 0. */
export function dayWindow(loc: HoursSource, now: Date | IsoDateTime, tz: string): DayWindow {
  const { date, min } = localDateMinutes(now, tz);
  const yesterday = addLocalDays(date, -1);
  const y = hoursOn(loc, yesterday);
  if (y !== "closed" && isOvernight(y) && min < toMinutes(y.close) + 60) return { date: yesterday, closed: false, nowMin: min + 1440, ...spanMinutes(y) };
  const h = hoursOn(loc, date);
  if (h === "closed") return { date, closed: true, nowMin: min, openMin: 0, closeMin: 0 };
  return { date, closed: false, nowMin: min, ...spanMinutes(h) };
}


/* ─────────────── validation (API, both demos) ─────────────── */

export const MAX_SPECIAL_DAYS = 400;
export const MAX_TEMPLATES = 30;
const TEMPLATE_ID = /^[a-z0-9_-]{1,40}$/;

/** One day's value from a request: "closed", { open, close }, or { open, close, ranges } (H6). `who` names it in the message. */
export function parseDayHours(d: unknown, who: string): { ok: true; day: DayHours } | { ok: false; message: string } {
  if (d === "closed") return { ok: true, day: "closed" };
  if (!d || typeof d !== "object" || Array.isArray(d)) return { ok: false, message: `${who}: give the hours or "closed".` };
  const { open, close, ranges } = d as Record<string, unknown>;
  if (ranges !== undefined && ranges !== null) {
    if (!Array.isArray(ranges)) return { ok: false, message: `${who}: "ranges" is a list of { open, close }.` };
    const p = rangesProblem(ranges as TimeRange[]);
    if (p) return { ok: false, message: `${who}: ${p.charAt(0).toLowerCase()}${p.slice(1)}` };
    return { ok: true, day: spanOfRanges(ranges as TimeRange[]) };
  }
  if (typeof open !== "string" || typeof close !== "string" || !HHMM.test(open) || !HHMM.test(close)) return { ok: false, message: `${who}: hours look like 07:00.` };
  if (open === close) return { ok: false, message: `${who}: opening and closing can't be the same time.` };
  return { ok: true, day: { open, close } };
}

/** "Thanksgiving, Thursday, November 26". */
export function dateWords(date: IsoDate): string {
  return new Date(`${date}T12:00:00Z`).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", timeZone: "UTC" });
}

/**
 * Checks a `hours` value from a request. null/undefined are handled by the caller (absent / clear).
 * The week is required; `dates` (H7) and `templates` (H8) are optional and kept only when not empty.
 */
export function parseWeeklyHours(v: unknown): { ok: true; hours: WeeklyHours } | { ok: false; message: string } {
  if (!v || typeof v !== "object" || Array.isArray(v)) return { ok: false, message: '"hours" must list mon to sun.' };
  const o = v as Record<string, unknown>;
  const extra = Object.keys(o).filter((k) => !(DAY_KEYS as readonly string[]).includes(k) && k !== "dates" && k !== "templates");
  if (extra.length) return { ok: false, message: `"hours" has an unknown day: ${extra[0]}. Use mon, tue, wed, thu, fri, sat, sun.` };
  const out = {} as WeeklyHours;
  for (const k of DAY_KEYS) {
    const r = parseDayHours(o[k], DAY_NAMES[k]);
    if (!r.ok) return r;
    out[k] = r.day;
  }
  if (DAY_KEYS.every((k) => out[k] === "closed")) return { ok: false, message: "Open at least one day." };
  if (o.dates !== undefined && o.dates !== null) {
    const d = parseSpecialDays(o.dates);
    if (!d.ok) return d;
    if (Object.keys(d.dates).length) out.dates = d.dates;
  }
  if (o.templates !== undefined && o.templates !== null) {
    const t = parseTemplates(o.templates);
    if (!t.ok) return t;
    if (t.templates.length) out.templates = t.templates;
  }
  return { ok: true, hours: out };
}

/** Special days from a request (H7): { "2026-11-26": { hours, label?, templateId? } }, at most MAX_SPECIAL_DAYS. */
export function parseSpecialDays(v: unknown): { ok: true; dates: Record<IsoDate, SpecialDay> } | { ok: false; message: string } {
  if (!v || typeof v !== "object" || Array.isArray(v)) return { ok: false, message: '"dates" is a list of days like { "2026-11-26": { "hours": "closed" } }.' };
  const keys = Object.keys(v as object);
  if (keys.length > MAX_SPECIAL_DAYS) return { ok: false, message: `At most ${MAX_SPECIAL_DAYS} special days.` };
  const out: Record<IsoDate, SpecialDay> = {};
  for (const k of keys.sort()) {
    if (!isDate(k) || Number.isNaN(Date.parse(`${k}T12:00:00Z`)) || new Date(`${k}T12:00:00Z`).toISOString().slice(0, 10) !== k) return { ok: false, message: `"${k}" isn't a date like 2026-11-26.` };
    const e = (v as Record<string, unknown>)[k];
    if (!e || typeof e !== "object" || Array.isArray(e)) return { ok: false, message: `${k}: give { "hours": … }.` };
    const x = e as Record<string, unknown>;
    const r = parseDayHours(x.hours, dateWords(k));
    if (!r.ok) return r;
    const label = typeof x.label === "string" ? x.label.trim().slice(0, 60) : "";
    const templateId = typeof x.templateId === "string" && TEMPLATE_ID.test(x.templateId) ? x.templateId : "";
    out[k] = { hours: r.day, ...(label ? { label } : {}), ...(templateId ? { templateId } : {}) };
  }
  return { ok: true, dates: out };
}

/** Saved templates from a request (H8): [{ id, name, hours }], at most MAX_TEMPLATES, ids unique. */
export function parseTemplates(v: unknown): { ok: true; templates: HoursTemplate[] } | { ok: false; message: string } {
  if (!Array.isArray(v)) return { ok: false, message: '"templates" is a list of { id, name, hours }.' };
  if (v.length > MAX_TEMPLATES) return { ok: false, message: `At most ${MAX_TEMPLATES} saved hours.` };
  const out: HoursTemplate[] = [];
  for (const t of v) {
    if (!t || typeof t !== "object") return { ok: false, message: "Each saved hours needs a name." };
    const x = t as Record<string, unknown>;
    const name = typeof x.name === "string" ? x.name.trim().slice(0, 60) : "";
    if (!name) return { ok: false, message: "Each saved hours needs a name." };
    if (typeof x.id !== "string" || !TEMPLATE_ID.test(x.id)) return { ok: false, message: `"${name}": its id is letters, numbers and dashes.` };
    if (out.some((o) => o.id === x.id)) return { ok: false, message: `"${name}" is listed twice.` };
    const r = parseDayHours(x.hours, name);
    if (!r.ok) return r;
    out.push({ id: x.id, name, hours: r.day });
  }
  return { ok: true, templates: out };
}

/**
 * The stored hours after a save (API and both demos): the incoming week, keeping the stored `dates` and `templates`
 * when the request left them out (an older phone that only knows the week never wipes the holidays). Special days
 * older than a year are dropped (the records of those days are already written; analytics reads 13 weeks).
 */
export function mergeHours(prev: WeeklyHours | null | undefined, next: WeeklyHours, sent: { dates: boolean; templates: boolean }, today?: IsoDate): WeeklyHours {
  const out: WeeklyHours = weekFrom((k) => next[k]);
  const dates = sent.dates ? next.dates : prev?.dates;
  const templates = sent.templates ? next.templates : prev?.templates;
  if (dates) {
    const cut = today ? addLocalDays(today, -366) : null;
    const kept = Object.fromEntries(Object.entries(dates).filter(([d]) => !cut || d >= cut));
    if (Object.keys(kept).length) out.dates = kept;
  }
  if (templates?.length) out.templates = templates.map((t) => ({ ...t }));
  return out;
}

/** The fallback pair to store with weekly hours: the first open day, Monday first (older clients read it). */
export function fallbackPair(h: WeeklyHours): OpenSpan {
  const d = DAY_KEYS.map((k) => h[k]).find((x): x is OpenSpan => x !== "closed");
  return d ? { open: d.open, close: d.close } : { open: "07:00", close: "15:00" };
}

/* ─────────────── the picker (web + phone) ─────────────── */

/** Default when nothing is known about the business: 7:00 to 3:00 every day. */
export const DEFAULT_SPAN: OpenSpan = { open: "07:00", close: "15:00" };

/** "Same every day". */
export function sameEveryDay(s: OpenSpan = DEFAULT_SPAN): WeeklyHours {
  return weekFrom(() => ({ ...s }));
}
/** "Mon–Fri + weekend": weekdays one way, Saturday and Sunday another (or closed). */
export function weekdaysAndWeekend(weekdays: OpenSpan, weekend: DayHours): WeeklyHours {
  return weekFrom((k) => (WEEKDAY_KEYS.includes(k) ? { ...weekdays } : weekend === "closed" ? "closed" : { ...weekend }));
}
/** "Copy Monday to all weekdays". */
export function copyMondayToWeekdays(h: WeeklyHours): WeeklyHours {
  const mon = h.mon;
  return { ...h, ...weekFrom((k) => (WEEKDAY_KEYS.includes(k) ? (mon === "closed" ? "closed" : { ...mon }) : h[k])) };
}
/** A location's week for the picker: its weekly hours, or its single pair every day (H1). */
export function weekOf(loc: Partial<HoursSource> | undefined): WeeklyHours {
  if (loc?.hours) return weekFrom((k) => weekdayHours(loc as HoursSource, k));
  return sameEveryDay(loc?.openAt && loc?.closeAt ? { open: loc.openAt, close: loc.closeAt } : DEFAULT_SPAN);
}

/** "7 AM", "3:30 PM", "12 PM". */
export function clock12(t: string): string {
  const m = toMinutes(t), h = Math.floor(m / 60), mm = m % 60;
  return `${h % 12 || 12}${mm ? `:${String(mm).padStart(2, "0")}` : ""} ${h < 12 ? "AM" : "PM"}`;
}
/** One day in words: "7 AM to 3 PM", "4 PM to 1 AM (next day)", "11 AM to 2 PM, 5 PM to 10 PM", "Closed". */
export function dayHoursText(d: DayHours): string {
  if (d === "closed") return "Closed";
  return rangesOf(d).map((r) => `${clock12(r.open)} to ${clock12(r.close)}${isOvernight(r) ? " (next day)" : ""}`).join(", ");
}
/** Two days with the same hours (ranges included). */
export function sameDayHours(a: DayHours, b: DayHours): boolean {
  if (a === "closed" || b === "closed") return a === b;
  const x = rangesOf(a), y = rangesOf(b);
  return x.length === y.length && x.every((r, i) => r.open === y[i]!.open && r.close === y[i]!.close);
}
/** "Copy to all days": every day gets `k`'s hours (special days and templates kept). */
export function copyDayToAll(h: WeeklyHours, k: DayKey): WeeklyHours {
  const d = h[k];
  return { ...h, ...weekFrom(() => (d === "closed" ? "closed" : spanOfRanges(rangesOf(d)))) };
}
/** The week in one line, runs of equal days joined: "Mon–Fri 7 AM to 3 PM · Sat 8 AM to 1 PM · Sun closed". */
export function hoursSummary(h: WeeklyHours): string {
  const same = sameDayHours;
  if (DAY_KEYS.every((k) => same(h[k], h.mon))) return `Every day ${h.mon === "closed" ? "closed" : dayHoursText(h.mon)}`;
  const parts: string[] = [];
  for (let i = 0; i < DAY_KEYS.length;) {
    let j = i;
    while (j + 1 < DAY_KEYS.length && same(h[DAY_KEYS[j + 1]!], h[DAY_KEYS[i]!])) j++;
    const d = h[DAY_KEYS[i]!];
    const days = i === j ? DAY_SHORT[DAY_KEYS[i]!] : `${DAY_SHORT[DAY_KEYS[i]!]}–${DAY_SHORT[DAY_KEYS[j]!]}`;
    parts.push(`${days} ${d === "closed" ? "closed" : dayHoursText(d)}`);
    i = j + 1;
  }
  return parts.join(" · ");
}
/** Which preset a week matches, for the picker's chips. */
export function presetOf(h: WeeklyHours): "same" | "weekdays" | "custom" {
  const eq = sameDayHours;
  // A split day (H6) is edited day by day: the presets set one span.
  if (DAY_KEYS.some((k) => h[k] !== "closed" && rangesOf(h[k] as OpenSpan).length > 1)) return "custom";
  if (DAY_KEYS.every((k) => eq(h[k], h.mon)) && h.mon !== "closed") return "same";
  if (WEEKDAY_KEYS.every((k) => eq(h[k], h.mon)) && h.mon !== "closed" && eq(h.sat, h.sun)) return "weekdays";
  return "custom";
}

/**
 * The calendar hours (0–23) of `date` the place is open for at least part of: the day's own span, plus the tail of
 * yesterday's overnight shift after midnight. Closed all day and no overnight tail: [].
 */
export function openHoursOn(loc: HoursSource, date: IsoDate): number[] {
  const out = new Set<number>();
  const y = hoursOn(loc, addLocalDays(date, -1));
  if (y !== "closed" && isOvernight(y)) for (let h = 0; h * 60 < toMinutes(y.close); h++) out.add(h);
  const t = hoursOn(loc, date);
  if (t !== "closed") {
    const { openMin, closeMin } = spanMinutes(t);
    for (let h = Math.floor(openMin / 60); h * 60 < Math.min(closeMin, 1440); h++) out.add(h);
  }
  return [...out].sort((a, b) => a - b);
}

/** Handwashing reminder times for a day (checkin.ts handwashNudges) from weekly hours: none on a closed day. */
export function handwashTimesOn(loc: HoursSource, date: IsoDate): string[] {
  const h = hoursOn(loc, date);
  if (h === "closed") return [];
  const { openMin, closeMin } = spanMinutes(h);
  const out: string[] = [];
  for (let m = openMin + 60; m < closeMin; m += 60) out.push(hhmm(m));
  return out;
}

/**
 * The demo cafés' week (web and phone mocks): today, yesterday and tomorrow keep `span` so the demo always looks
 * mid-shift with yesterday's records; two days out is a short day (closes 5 hours after opening); three days out is
 * closed all day. So every demo week has a short day and a closed day, and neither is ever the day on screen.
 */
export function demoWeeklyHours(today: IsoDate, span: OpenSpan): WeeklyHours {
  const h = sameEveryDay(span);
  const { openMin, closeMin } = spanMinutes(span);
  if (closeMin - openMin > 5 * 60) h[dayKeyOf(addLocalDays(today, 2))] = { open: span.open, close: hhmm(openMin + 5 * 60) };
  h[dayKeyOf(addLocalDays(today, 3))] = "closed";
  return h;
}

/** The next opening after `now` (today's, if it hasn't opened yet), within a week; null if never open. */
export function nextOpening(loc: HoursSource, now: Date | IsoDateTime, tz: string): { date: IsoDate; open: string; day: DayKey; tomorrow: boolean } | null {
  const { date, min } = localDateMinutes(now, tz);
  for (let i = 0; i <= 7; i++) {
    const d = addLocalDays(date, i), h = hoursOn(loc, d);
    if (h === "closed" || (i === 0 && toMinutes(h.open) <= min)) continue;
    return { date: d, open: h.open, day: dayKeyOf(d), tomorrow: i === 1 };
  }
  return null;
}

/**
 * A location save's hours fields, one rule for the API and both demos: `hours` (the week; null = one pair every day),
 * `specialDays` (H7) and `hoursTemplates` (H8). What the request leaves out is kept (mergeHours). Returns the fields to
 * store (`hours: null` = remove it), or the plain problem.
 */
export function hoursPatch(loc: HoursSource, b: { hours?: unknown; specialDays?: unknown; hoursTemplates?: unknown; openAt?: string; closeAt?: string }, today: IsoDate):
  { ok: true; set: { hours?: WeeklyHours | null; openAt?: string; closeAt?: string } } | { ok: false; message: string } {
  const set: { hours?: WeeklyHours | null; openAt?: string; closeAt?: string } = {};
  if (b.hours === null) set.hours = loc.hours?.dates || loc.hours?.templates ? mergeHours(loc.hours, weekOf({ openAt: b.openAt ?? loc.openAt, closeAt: b.closeAt ?? loc.closeAt }), { dates: false, templates: false }, today) : null;
  else if (b.hours !== undefined) {
    const r = parseWeeklyHours(b.hours);
    if (!r.ok) return r;
    const sent = b.hours as Record<string, unknown>;
    set.hours = mergeHours(loc.hours, r.hours, { dates: "dates" in sent, templates: "templates" in sent }, today);
    const p = fallbackPair(r.hours);
    set.openAt = p.open; set.closeAt = p.close;
  }
  if (b.specialDays !== undefined || b.hoursTemplates !== undefined) {
    const base = set.hours ?? loc.hours ?? weekOf(loc);
    let dates = base.dates, templates = base.templates;
    if (b.specialDays !== undefined) { const r = parseSpecialDays(b.specialDays ?? {}); if (!r.ok) return r; dates = r.dates; }
    if (b.hoursTemplates !== undefined) { const r = parseTemplates(b.hoursTemplates ?? []); if (!r.ok) return r; templates = r.templates; }
    set.hours = mergeHours(base, { ...base, dates, templates }, { dates: true, templates: true }, today);
  }
  return { ok: true, set };
}
